import type { LucideIcon } from "lucide-react";
import { Brain, HelpCircle, Target } from "lucide-react";
import coachingImg from "../../image/education/education-intro-coaching.png";
import { DataKeys } from "../../types";
import { useLang } from "../Context";
import { getLocalized, HealthLang, LocalizedString } from "../health/healthTypes";

const calloutIconMap: Record<string, LucideIcon> = {
    question: HelpCircle,
    reasoning: Brain,
    improve: Target,
};

const calloutPositions = [
    "left-[-1%] top-[4%]",
    "right-[-1%] top-[0%]",
    "bottom-[4%] right-[0%]",
] as const;

interface EducationIntroVisualProps {
    callouts: DataKeys[];
    visualAlt: LocalizedString | undefined;
}

export function EducationIntroVisual({ callouts, visualAlt }: EducationIntroVisualProps) {
    const { lang } = useLang();
    const hLang = lang as HealthLang;

    return (
        <div className="relative mx-auto w-full min-w-0 max-w-[34rem] sm:max-w-[38rem] lg:mx-0 lg:max-w-none">
            <div className="relative flex h-[15rem] w-full items-center justify-center overflow-hidden bg-white sm:h-[19rem] lg:h-[22rem] xl:h-[24rem]">
                <img
                    src={coachingImg}
                    alt={getLocalized(visualAlt, hLang)}
                    className="h-full w-full max-w-none origin-center scale-[1.26] object-contain object-center sm:scale-[1.3] lg:scale-[1.34]"
                    loading="lazy"
                />
            </div>

            {callouts.map((callout, index) => {
                const iconKey = (callout.id as string) ?? "question";
                const Icon = calloutIconMap[iconKey] ?? HelpCircle;
                const position = calloutPositions[index] ?? calloutPositions[0];

                return (
                    <div
                        key={(callout.id as string) ?? index}
                        className={`absolute z-[2] flex w-[5.75rem] flex-col items-center gap-2 rounded-2xl bg-white px-2.5 py-3 text-center shadow-[0_12px_32px_rgba(3,124,132,0.18)] sm:w-[6.25rem] sm:px-3 ${position}`}
                    >
                        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-teal-50 text-teal-500">
                            <Icon className="h-6 w-6" strokeWidth={1.75} aria-hidden="true" />
                        </span>
                        <span className="text-center text-[10px] font-semibold leading-snug text-grey-800 sm:text-[11px]">
                            {getLocalized(callout.label as LocalizedString, hLang)}
                        </span>
                    </div>
                );
            })}
        </div>
    );
}
